import { Autocomplete, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import style from "../../../assets/styles/componentStyles/feedbackStyles/feedbackStyle.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { formatDateAccordingToTimezone, formatDateAccordingToTimezoneUsingMoment, getUserFullName, isAdmin } from '../../../utils/constants';
import { primaryColor } from '../../../utils/colors';
import { callAnalysisFeedbackDataResponse, feedback_data, feedbackKnowledgeBaseStatus, feedbackStatus, getCallAnalysisFeedbackResponseModel, getFeedbackResponse, organizationUserDataModel, updateFeedbackPayload } from '../../../models/axiosModel';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { GenericDataContext, GenericDataContextPayload } from '../../../context/genericDataContext';
import agent from '../../../services/api';
import GlobalButton from '../../utility/globalButton/button';
import { useNavigate } from 'react-router-dom';
import IconsColor from '../../utility/iconsColor';
import chronox_icon from "../../../assets/images/global/Chronox AI Icon.png";
import { CallAnalysisContext, CallAnalysisContextPayload } from '../../../context/callAnalysisContext';
import { getVendorField } from '../../../utils/whiteLabelling';

interface FeedbackTableProps {
    feedbackData: (getFeedbackResponse | getCallAnalysisFeedbackResponseModel)[],
    setFeedbackData: React.Dispatch<React.SetStateAction<(getFeedbackResponse | getCallAnalysisFeedbackResponseModel)[]>>,
    page: number,
    rowsPerPage: number,
    onTableRowClick: (rowData: getFeedbackResponse | getCallAnalysisFeedbackResponseModel) => void;
    updateFeedbackApiCall: (feedback_id: number, status_id: number, modification_status_id: number, reviewer_note: string | null, admin_note: string | null, isCallAnalysisFeedback: boolean, private_user_id?: number | null) => void;
}

const FeedbackTable: React.FC<FeedbackTableProps> = ({ feedbackData, setFeedbackData, page, rowsPerPage, onTableRowClick, updateFeedbackApiCall }) => {

    const { userDetail, organizationDetails, vendorData } = useContext(UserContext) as UserContextPayload
    const { feedbackStatusData, feedbackKnowledgeBaseStatusData } = useContext(GenericDataContext) as GenericDataContextPayload;
    const { GetCallRecordDetailApi } = useContext(CallAnalysisContext) as CallAnalysisContextPayload

    const previousVal = useRef("");

    const onRowClick = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel) => {
        if (isAdmin(userDetail)) {
            onTableRowClick(item);
        }
    }

    const renderType = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel): JSX.Element | string => {
        if (item && "feedback_id" in item) {
            switch (item.feedback_level_name.toLowerCase()) {
                case "chronox":
                    return (
                        <img src={getVendorField(vendorData, "company primary logo favicon", chronox_icon)} alt="Logo" height={"18px"} width={"18px"} />
                    );
                case "chatbot":
                    return (
                        <IconsColor iconName="chatbot" fillColor={primaryColor} width={"18px"} height={"18px"} />
                    );
                case "thread":
                case "message":
                    return (
                        <IconsColor iconName="chat" fillColor={primaryColor} width={"18px"} height={"18px"} />
                    );
                default:
                    return "";
            }
        } else {
            return (
                <IconsColor iconName="callAnalysis" fillColor={primaryColor} width={"18px"} height={"18px"} />
            );
        }
    }

    const renderMoreDetails = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel): JSX.Element | string => {
        if (item && "feedback_id" in item) {
            if (item.feedback_data?.length > 0 && item.feedback_data[0].feedback_option_field_value != "") {
                return (
                    <Grid container className={globalStyles.flexColumn} >
                        {item.feedback_data.map((data: feedback_data, ind: number) => {
                            return (
                                <Grid item key={ind}>
                                    <span className={globalStyles.primaryFontWeight} >{data.feedback_option_field_value}: <span className={globalStyles.secondaryFontWeight}>{data.feedback_value}</span> </span>
                                </Grid>
                            );
                        })}
                    </Grid>
                );
            } else {
                return "-";
            }
        } else {
            if (item?.call_analysis_feedback_data?.length > 0 && item?.call_analysis_feedback_data?.[0]?.call_analysis_feedback_option_field_value != "") {
                return (
                    <Grid container className={globalStyles.flexColumn}>
                        {item.call_analysis_feedback_data.map((data: callAnalysisFeedbackDataResponse, ind: number) => {
                            return (
                                <Grid item key={ind}>
                                    <span className={globalStyles.primaryFontWeight} >{data.call_analysis_feedback_option_field_value}: <span className={globalStyles.secondaryFontWeight}>{data.call_analysis_feedback_data_value}</span> </span>
                                </Grid>
                            );
                        })}
                    </Grid>
                );
            } else {
                return "-";
            }
        }
    }

    const onChangeOfFeedbackStatus = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, value: feedbackStatus) => {
        if (item && "feedback_id" in item) {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "feedback_id" in data) {
                        return data.feedback_id === item.feedback_id ? { ...data, feedback_status_name: value.feedback_status_name, feedback_status_id: value.feedback_status_id } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.feedback_id, value.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, false);
        } else {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "call_analysis_feedback_id" in data) {
                        return data.call_analysis_feedback_id === item.call_analysis_feedback_id ? { ...data, feedback_status_name: value.feedback_status_name, feedback_status_id: value.feedback_status_id } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.call_analysis_feedback_id, value.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, true);
        }
    }

    const onChangeOfModificationStatus = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, value: feedbackKnowledgeBaseStatus) => {
        if (item && "feedback_id" in item) {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "feedback_id" in data) {
                        return data.feedback_id === item.feedback_id ? { ...data, modification_status_name: value.modification_status_name, modification_status_id: value.modification_status_id } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.feedback_id, item.feedback_status_id, value.modification_status_id, item.reviewer_note, item.admin_note, false);
        } else {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "call_analysis_feedback_id" in data) {
                        return data.call_analysis_feedback_id === item.call_analysis_feedback_id ? { ...data, modification_status_name: value.modification_status_name, modification_status_id: value.modification_status_id } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.call_analysis_feedback_id, item.feedback_status_id, value.modification_status_id, item.reviewer_note, item.admin_note, true);
        }
    }

    const onChangeOfReviewerNote = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, value: string) => {
        if (item && "feedback_id" in item) {
            setFeedbackData(prev =>
                prev.map(data => {
                    if (data && "feedback_id" in data) {
                        return data.feedback_id === item.feedback_id ? { ...data, reviewer_note: value } : data
                    }
                    return data;
                })
            )
        } else {
            setFeedbackData(prev =>
                prev.map(data => {
                    if (data && "call_analysis_feedback_id" in data) {
                        return data.call_analysis_feedback_id === item.call_analysis_feedback_id ? { ...data, reviewer_note: value } : data
                    }
                    return data;
                })
            )
        }
    }

    const onBlurOfReviewerNote = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, currentVal: string) => {
        if (currentVal != previousVal.current) {
            if (item && "feedback_id" in item) {
                updateFeedbackApiCall(item.feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, false);
            } else {
                updateFeedbackApiCall(item.call_analysis_feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, true);
            }
        }
    }

    const onChangeOfAdminNote = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, value: string) => {
        if (item && "feedback_id" in item) {
            setFeedbackData(prev =>
                prev.map(data => {
                    if (data && "feedback_id" in data) {
                        return data.feedback_id === item.feedback_id ? { ...data, admin_note: value } : data
                    }
                    return data;
                })
            )
        } else {
            setFeedbackData(prev =>
                prev.map(data => {
                    if (data && "call_analysis_feedback_id" in data) {
                        return data.call_analysis_feedback_id === item.call_analysis_feedback_id ? { ...data, admin_note: value } : data
                    }
                    return data;
                })
            )
        }
    }

    const onBlurOfAdminNote = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, currentVal: string) => {
        if (currentVal != previousVal.current) {
            if (item && "feedback_id" in item) {
                updateFeedbackApiCall(item.feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, false)
            } else {
                updateFeedbackApiCall(item.call_analysis_feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, true)
            }
        }
    }

    const onChangeOfAssignMember = (item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, value: organizationUserDataModel | null) => {
        const callAnalytsisUpdatedValue = {
            created_at_timestamp: "",
            call_analysis_feedback_assign_user_id: "",
            call_analysis_feedback_id: (item && "call_analysis_feedback_id" in item) ? item.call_analysis_feedback_id : 0,
            private_user_first_name: value ? value.private_user_first_name : "",
            private_user_id: value ? value.private_user_id : 0,
            private_user_last_name: value ? value.private_user_last_name : "",
        }

        const feedbackUpdatedValue = {
            created_at_timestamp: "",
            feedback_assign_user_id: "",
            feedback_id: (item && "feedback_id" in item) ? item.feedback_id : 0,
            private_user_first_name: value ? value.private_user_first_name : "",
            private_user_id: value ? value.private_user_id : 0,
            private_user_last_name: value ? value.private_user_last_name : "",
        }
        if (item && "feedback_id" in item) {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "feedback_id" in data) {
                        return data.feedback_id === item.feedback_id ? { ...data, feedback_assign_user_data: value ? [feedbackUpdatedValue] : [] } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, false, value?.private_user_id);
        } else {
            setFeedbackData(prev =>
                prev.map((data) => {
                    if (data && "call_analysis_feedback_id" in data) {
                        return data.call_analysis_feedback_id === item.call_analysis_feedback_id ? { ...data, call_analysis_feedback_assign_user_data: value ? [callAnalytsisUpdatedValue] : [] } : data
                    }
                    return data;
                })
            )
            updateFeedbackApiCall(item.call_analysis_feedback_id, item.feedback_status_id, item.modification_status_id, item.reviewer_note, item.admin_note, true, value?.private_user_id);
        }
    }
    const navigate = useNavigate();

    const handleViewCallRecord = async (item: getCallAnalysisFeedbackResponseModel) => {
        window.open(`/callanalysis/callrecords?feedback_id=${item.call_record_id}`, '_blank');
        // GetCallRecordDetailApi(item.call_record_id)
    }

    const findMemberById = (id: number | null) => {
        if (id) {
            const member = organizationDetails?.[0]?.organization_user_data?.find((user: organizationUserDataModel) => user.private_user_id == id);

            if (member) {
                if (member.private_user_first_name || member.private_user_last_name) {
                    return (member.private_user_first_name ? member.private_user_first_name : "") + (member.private_user_last_name ? " " + member.private_user_last_name : "")
                } else {
                    return member.private_user_email;
                }
            } else {
                return "";
            }
        } else {
            return "";
        }
    }

    const innerHeight = (window.innerHeight - 300);

    const sortFeedbackData = () => {
        return feedbackData.sort((a, b) => new Date(b.created_at_timestamp).getTime() - new Date(a.created_at_timestamp).getTime()).slice((page * rowsPerPage), (page * rowsPerPage) + rowsPerPage)
    }

    useEffect(() => {
        sortFeedbackData()
    }, [page])


    return (
        <>
            <TableContainer className={style.feedbackTableContainer} sx={{ maxHeight: `${innerHeight}px !important` }}>
                <Table aria-label="simple table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Type</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Source</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Date</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Provided Type</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Provider</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Category</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>More Detail</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Approval Status</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Modification Status</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Note By Reviewer</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Note By Admin</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Assign Member</TableCell>
                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>View</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortFeedbackData()?.length > 0 ?
                            sortFeedbackData().map((item: getFeedbackResponse | getCallAnalysisFeedbackResponseModel, ind: number) => {
                                return (
                                    <React.Fragment key={ind}>
                                        <TableRow onClick={() => { onRowClick(item) }} className={dashboardStyles.cellRow} sx={{ cursor: isAdmin(userDetail) ? "pointer" : "default" }}>
                                            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${globalStyles.textCenter}`} >{renderType(item)}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{(item && "chatbot_name" in item) ? item.chatbot_name : item?.call_record_google_storage_file_name}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{formatDateAccordingToTimezoneUsingMoment(item?.created_at_timestamp, userDetail.timezone_hour)}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{(item && "feedback_provided_type_name" in item) ? item.feedback_provided_type_name : "-"}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{(item && "feedback_provider" in item) ? item.feedback_provider != "" ? item.feedback_provider : "-" : "-"}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{(item && "feedback_option_name" in item) ? item.feedback_option_name : item?.call_analysis_feedback_option_name}</TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}> {renderMoreDetails(item)} </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {isAdmin(userDetail) ?
                                                    <>
                                                        <Autocomplete
                                                            options={feedbackStatusData}
                                                            value={feedbackStatusData.find((data) => data?.feedback_status_id === item?.feedback_status_id)}
                                                            getOptionLabel={(option) => option?.feedback_status_name}
                                                            onChange={(e, value) => { onChangeOfFeedbackStatus(item, value); }}
                                                            className={`${style.feedbackTableAutoComplete}`}
                                                            disableClearable={true}
                                                            renderInput={(params) => <TextField {...params} placeholder="Select Status" />}
                                                        />
                                                    </>
                                                    :
                                                    item.feedback_status_name
                                                }
                                            </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {isAdmin(userDetail) ?
                                                    <>
                                                        <Autocomplete
                                                            options={feedbackKnowledgeBaseStatusData}
                                                            getOptionLabel={(option) => option.modification_status_name}
                                                            value={feedbackKnowledgeBaseStatusData?.find((data) => data?.modification_status_id === item?.modification_status_id)}
                                                            onChange={(e, value) => { onChangeOfModificationStatus(item, value); }}
                                                            className={`${style.feedbackTableAutoComplete}`}
                                                            disableClearable={true}
                                                            renderInput={(params) => <TextField {...params} placeholder="Select Status" />}
                                                        />
                                                    </>
                                                    :
                                                    item.modification_status_name
                                                }
                                            </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {isAdmin(userDetail) ?
                                                    <>
                                                        <Tooltip title={item?.reviewer_note ? item?.reviewer_note : ""}>
                                                            <TextField
                                                                value={item?.reviewer_note ? item.reviewer_note : ""}
                                                                className={`${style.feedbackTableTextField}`}
                                                                onChange={(e) => { onChangeOfReviewerNote(item, e.target.value); }}
                                                                onBlur={(e) => { onBlurOfReviewerNote(item, e.target.value); }}
                                                                onFocus={(e) => previousVal.current = e.target.value}
                                                            />
                                                        </Tooltip>
                                                    </>
                                                    :
                                                    item.reviewer_note ? item.reviewer_note : "-"
                                                }
                                            </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {isAdmin(userDetail) ?
                                                    <>
                                                        <Tooltip title={item?.admin_note ? item.admin_note : ""}>
                                                            <TextField
                                                                value={item?.admin_note ? item.admin_note : ""}
                                                                className={`${style.feedbackTableTextField}`}
                                                                onChange={(e) => { onChangeOfAdminNote(item, e.target.value); }}
                                                                onBlur={(e) => { onBlurOfAdminNote(item, e.target.value); }}
                                                                onFocus={(e) => previousVal.current = e.target.value}
                                                            />
                                                        </Tooltip>
                                                    </>
                                                    :
                                                    item.admin_note ? item.admin_note : "-"
                                                }
                                            </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {isAdmin(userDetail) ?
                                                    <>
                                                        <Autocomplete
                                                            options={organizationDetails?.[0].organization_user_data || []}
                                                            getOptionLabel={(option) => getUserFullName(option)}
                                                            value={organizationDetails?.[0]?.organization_user_data?.find((member: organizationUserDataModel) => member.private_user_id == ("feedback_id" in item ? item?.feedback_assign_user_data?.[0]?.private_user_id : item?.call_analysis_feedback_assign_user_data?.[0]?.private_user_id))}
                                                            onChange={(e, value) => { onChangeOfAssignMember(item, value) }}
                                                            renderInput={(params) => <TextField {...params} placeholder="Select Member" />}
                                                            className={`${style.feedbackTableAutoComplete} ${style.feedbackAssignMemberAutoComplete}`}
                                                        />
                                                    </>
                                                    :
                                                    findMemberById("feedback_id" in item ? item.feedback_assign_user_data?.[0]?.private_user_id || null : item.call_analysis_feedback_assign_user_data?.[0]?.private_user_id || null)
                                                }
                                            </TableCell>
                                            <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle} onClick={(e) => e.stopPropagation()}>
                                                {(item && "feedback_id" in item) ?
                                                    // (item?.chatbot_user_id || (item?.feedback_level_name === "Thread" && item?.feedback_unique_id)) ?
                                                    //     <GlobalButton
                                                    //         buttonText={"View"}
                                                    //         className='primaryButtonStyle'
                                                    //         sx={{ width: "100px" }}
                                                    //         onClick={() => { window.open(`${window.location.origin}/chatbot/viewchatbot/inbox?chatbot_id=${item?.chatbot_id}&chatbot_user_id=${item.chatbot_user_id ? item.chatbot_user_id : item?.feedback_unique_id}&feedback_id=${item.feedback_id}`, "_blank") }}
                                                    //     />
                                                    //     : 
                                                        "NA"
                                                    : (item && "call_analysis_feedback_id" in item) ?
                                                        <GlobalButton
                                                            buttonText={"View"}
                                                            className='primaryButtonStyle'
                                                            sx={{ width: "100px" }}
                                                            onClick={() => handleViewCallRecord(item)}
                                                        />
                                                        :
                                                        "NA"
                                                }
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })
                            :
                            <TableRow className={dashboardStyles.cellRow}>
                                <TableCell colSpan={12} className={globalStyles.textCenter} >
                                    No Data Available
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default FeedbackTable
